/**
 * Granular permission keys. Enforced deny-by-default in the API.
 * The Administrator system role bypasses all checks.
 */
export const PERMISSION_GROUPS = {
  customers: ["view", "create", "edit", "delete"],
  requests: [
    "view_own",
    "view_all",
    "create",
    "edit_own",
    "edit_all",
    "delete",
    "assign",
    "change_status",
  ],
  offers: ["view", "create", "edit", "delete", "send", "change_status"],
  invoices: ["view", "create", "edit", "delete", "send", "change_status"],
  payments: ["record", "edit", "delete"],
  files: ["view", "upload", "delete"],
  documents: ["download"],
  users: [
    "view",
    "create",
    "edit",
    "deactivate",
    "send_password_reset",
    "change_email",
  ],
  employees: ["view_self", "view_all", "edit", "offboard"],
  projects: [
    "view_assigned",
    "view_all",
    "create",
    "edit_all",
    "delete",
    "manage_members_all",
    "manage_planning_all",
    "view_costs_all",
    "manage_costs_all",
    "manage_role_permissions",
  ],
  inventory: [
    "view",
    "manage_catalog",
    "manage_locations",
    "receive",
    "adjust",
    "transfer",
    "issue",
  ],
  teams: ["view", "manage"],
  expenses: ["view_own", "submit", "view_all", "pay", "ai"],
  automations: ["view", "manage"],
  time: ["record", "view_own", "view_all", "manage", "approve"],
  employee_documents: ["view_own", "manage"],
  roles: ["view", "manage"],
  settings: [
    "view",
    "company.edit",
    "billing.edit",
    "documents.edit",
    "system.edit",
  ],
  finance: ["view"],
  ai: ["use"],
  reports: ["view", "export"],
} as const;

export type Permission =
  | `customers.${(typeof PERMISSION_GROUPS.customers)[number]}`
  | `requests.${(typeof PERMISSION_GROUPS.requests)[number]}`
  | `offers.${(typeof PERMISSION_GROUPS.offers)[number]}`
  | `invoices.${(typeof PERMISSION_GROUPS.invoices)[number]}`
  | `payments.${(typeof PERMISSION_GROUPS.payments)[number]}`
  | `files.${(typeof PERMISSION_GROUPS.files)[number]}`
  | `documents.${(typeof PERMISSION_GROUPS.documents)[number]}`
  | `users.${(typeof PERMISSION_GROUPS.users)[number]}`
  | `employees.${(typeof PERMISSION_GROUPS.employees)[number]}`
  | `projects.${(typeof PERMISSION_GROUPS.projects)[number]}`
  | `inventory.${(typeof PERMISSION_GROUPS.inventory)[number]}`
  | `teams.${(typeof PERMISSION_GROUPS.teams)[number]}`
  | `expenses.${(typeof PERMISSION_GROUPS.expenses)[number]}`
  | `automations.${(typeof PERMISSION_GROUPS.automations)[number]}`
  | `time.${(typeof PERMISSION_GROUPS.time)[number]}`
  | `employee_documents.${(typeof PERMISSION_GROUPS.employee_documents)[number]}`
  | `roles.${(typeof PERMISSION_GROUPS.roles)[number]}`
  | `settings.${(typeof PERMISSION_GROUPS.settings)[number]}`
  | `finance.${(typeof PERMISSION_GROUPS.finance)[number]}`
  | `reports.${(typeof PERMISSION_GROUPS.reports)[number]}`
  | `ai.${(typeof PERMISSION_GROUPS.ai)[number]}`;

export const ALL_PERMISSIONS: readonly Permission[] = Object.entries(
  PERMISSION_GROUPS,
).flatMap(([group, actions]) =>
  actions.map((a) => `${group}.${a}` as Permission),
);

/** Default permission sets for the seeded non-admin roles. */
export const DEFAULT_ROLE_PRESETS: Record<string, readonly Permission[]> = {
  employee: [
    "customers.view",
    "customers.create",
    "customers.edit",
    "requests.view_own",
    "requests.create",
    "requests.edit_own",
    "files.view",
    "projects.view_assigned",
    "expenses.view_own",
    "expenses.submit",
    "expenses.ai",
    "files.upload",
    "ai.use",
    "employees.view_self",
    "time.record",
    "time.view_own",
    "employee_documents.view_own",
  ],
  office: [
    "customers.view",
    "customers.create",
    "customers.edit",
    "customers.delete",
    "requests.view_own",
    "requests.view_all",
    "requests.create",
    "requests.edit_own",
    "requests.edit_all",
    "requests.assign",
    "requests.change_status",
    "offers.view",
    "offers.create",
    "offers.edit",
    "offers.send",
    "offers.change_status",
    "invoices.view",
    "invoices.create",
    "invoices.edit",
    "invoices.send",
    "invoices.change_status",
    "finance.view",
    "payments.record",
    "projects.view_assigned",
    "projects.view_all",
    "inventory.view",
    "expenses.view_own",
    "expenses.submit",
    "expenses.view_all",
    "expenses.pay",
    "expenses.ai",
    "automations.view",
    "automations.manage",
    "users.send_password_reset",
    "users.change_email",
    "teams.view",
    "teams.manage",
    "payments.edit",
    "files.view",
    "files.upload",
    "files.delete",
    "documents.download",
    "reports.view",
    "ai.use",
    "employees.view_self",
    "employees.view_all",
    "employees.edit",
    "employees.offboard",
    "time.record",
    "time.view_own",
    "time.view_all",
    "time.manage",
    "time.approve",
    "employee_documents.view_own",
    "employee_documents.manage",
  ],
};

export function isPermission(value: unknown): value is Permission {
  return (
    typeof value === "string" &&
    (ALL_PERMISSIONS as readonly string[]).includes(value)
  );
}
